import type { StructureModel } from '../nbt/types';
import type { AtlasSlot, TextureAtlas } from '../textures/atlas';
import type { BlockAppearance, BlockBox } from '../textures/blockAppearance';

export interface GeometryData {
  positions: Float32Array;
  uvs: Float32Array;
  colors: Float32Array;
  indices: Uint32Array;
  /** 面ごとの、元になった`model.blocks`の位置。クリック位置からブロックを引くために使う。 */
  blockIndices: Uint32Array;
  faceCount: number;
}

export interface BuiltGeometry {
  solid: GeometryData | null;
  translucent: GeometryData | null;
  faceCount: number;
}

export type RenderMode = 'texture' | 'color';

/** Minecraftと同じ、面の向きだけで決まる固定の明るさ。 */
const FACE_SHADE = [0.6, 0.6, 1, 0.5, 0.8, 0.8];
const AO_SHADE = [0.45, 0.62, 0.8, 1];

const NEIGHBOR: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

/** 面ごとの、AOを測るための接線方向（u軸・v軸）。 */
const TANGENTS: ReadonlyArray<readonly [readonly [number, number, number], readonly [number, number, number]]> = [
  [[0, 0, -1], [0, 1, 0]],
  [[0, 0, 1], [0, 1, 0]],
  [[1, 0, 0], [0, 0, -1]],
  [[1, 0, 0], [0, 0, 1]],
  [[1, 0, 0], [0, 1, 0]],
  [[-1, 0, 0], [0, 1, 0]],
];

function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

const SHADE_LOOKUP = new Float32Array(1024);
for (let index = 0; index < SHADE_LOOKUP.length; index += 1) {
  SHADE_LOOKUP[index] = srgbToLinear(index / (SHADE_LOOKUP.length - 1));
}

function linearShade(value: number): number {
  return SHADE_LOOKUP[Math.round(Math.min(1, Math.max(0, value)) * (SHADE_LOOKUP.length - 1))];
}

class MeshBuffers {
  positions = new Float32Array(4096 * 3);
  uvs = new Float32Array(4096 * 2);
  colors = new Float32Array(4096 * 3);
  indices = new Uint32Array(6144);
  blockIndices = new Uint32Array(1024);
  vertexCount = 0;
  indexCount = 0;

  private grow() {
    if ((this.vertexCount + 4) * 3 > this.positions.length) {
      const positions = new Float32Array(this.positions.length * 2);
      positions.set(this.positions);
      this.positions = positions;
      const uvs = new Float32Array(this.uvs.length * 2);
      uvs.set(this.uvs);
      this.uvs = uvs;
      const colors = new Float32Array(this.colors.length * 2);
      colors.set(this.colors);
      this.colors = colors;
    }
    if (this.indexCount + 6 > this.indices.length) {
      const indices = new Uint32Array(this.indices.length * 2);
      indices.set(this.indices);
      this.indices = indices;
    }
    if (this.indexCount / 6 + 1 > this.blockIndices.length) {
      const blockIndices = new Uint32Array(this.blockIndices.length * 2);
      blockIndices.set(this.blockIndices);
      this.blockIndices = blockIndices;
    }
  }

  addQuad(corners: number[], uv: number[], shades: number[], red: number, green: number, blue: number, blockIndex: number) {
    this.grow();
    const base = this.vertexCount;
    for (let corner = 0; corner < 4; corner += 1) {
      const shade = shades[corner];
      this.positions[(base + corner) * 3] = corners[corner * 3];
      this.positions[(base + corner) * 3 + 1] = corners[corner * 3 + 1];
      this.positions[(base + corner) * 3 + 2] = corners[corner * 3 + 2];
      this.uvs[(base + corner) * 2] = uv[corner * 2];
      this.uvs[(base + corner) * 2 + 1] = uv[corner * 2 + 1];
      this.colors[(base + corner) * 3] = red * shade;
      this.colors[(base + corner) * 3 + 1] = green * shade;
      this.colors[(base + corner) * 3 + 2] = blue * shade;
    }
    this.vertexCount += 4;

    // AOの段差が三角形の切り方で目立たないよう、暗い対角で分割する。
    const flip = shades[0] + shades[2] > shades[1] + shades[3];
    const order = flip ? [0, 1, 3, 1, 2, 3] : [0, 1, 2, 0, 2, 3];
    for (let index = 0; index < 6; index += 1) {
      this.indices[this.indexCount + index] = base + order[index];
    }
    this.blockIndices[this.indexCount / 6] = blockIndex;
    this.indexCount += 6;
  }

  toGeometry(): GeometryData | null {
    if (this.vertexCount === 0) return null;
    return {
      positions: this.positions.subarray(0, this.vertexCount * 3),
      uvs: this.uvs.subarray(0, this.vertexCount * 2),
      colors: this.colors.subarray(0, this.vertexCount * 3),
      indices: this.indices.subarray(0, this.indexCount),
      blockIndices: this.blockIndices.subarray(0, this.indexCount / 6),
      faceCount: this.indexCount / 6,
    };
  }
}

function faceCorners(face: number, box: BlockBox, x: number, y: number, z: number): number[] {
  const x0 = x + box.from[0];
  const y0 = y + box.from[1];
  const z0 = z + box.from[2];
  const x1 = x + box.to[0];
  const y1 = y + box.to[1];
  const z1 = z + box.to[2];

  switch (face) {
    case 0: return [x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1];
    case 1: return [x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0];
    case 2: return [x0, y1, z1, x1, y1, z1, x1, y1, z0, x0, y1, z0];
    case 3: return [x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1];
    case 4: return [x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1];
    default: return [x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0];
  }
}

/** ボックスの寸法に合わせてテクスチャを切り出す。半ブロックなら下半分だけが使われる。 */
function faceUv(face: number, box: BlockBox, slot: AtlasSlot): number[] {
  const [fx, fy, fz] = box.from;
  const [tx, ty, tz] = box.to;
  let u0: number;
  let u1: number;
  let v0: number;
  let v1: number;

  switch (face) {
    case 0: u0 = 1 - tz; u1 = 1 - fz; v0 = fy; v1 = ty; break;
    case 1: u0 = fz; u1 = tz; v0 = fy; v1 = ty; break;
    case 2: u0 = fx; u1 = tx; v0 = 1 - tz; v1 = 1 - fz; break;
    case 3: u0 = fx; u1 = tx; v0 = fz; v1 = tz; break;
    case 4: u0 = fx; u1 = tx; v0 = fy; v1 = ty; break;
    default: u0 = 1 - tx; u1 = 1 - fx; v0 = fy; v1 = ty; break;
  }

  const su0 = slot.u0 + (slot.u1 - slot.u0) * u0;
  const su1 = slot.u0 + (slot.u1 - slot.u0) * u1;
  const sv0 = slot.v0 + (slot.v1 - slot.v0) * v0;
  const sv1 = slot.v0 + (slot.v1 - slot.v0) * v1;
  return [su0, sv0, su1, sv0, su1, sv1, su0, sv1];
}

function crossQuads(x: number, y: number, z: number): number[][] {
  const low = 0.1464;
  const high = 0.8536;
  return [
    [x + low, y, z + low, x + high, y, z + high, x + high, y + 1, z + high, x + low, y + 1, z + low],
    [x + high, y, z + high, x + low, y, z + low, x + low, y + 1, z + low, x + high, y + 1, z + high],
    [x + low, y, z + high, x + high, y, z + low, x + high, y + 1, z + low, x + low, y + 1, z + high],
    [x + high, y, z + low, x + low, y, z + high, x + low, y + 1, z + high, x + high, y + 1, z + low],
  ];
}

export interface BuildOptions {
  mode?: RenderMode;
  ambientOcclusion?: boolean;
  /** 指定があると、含まれないstateのブロックを灰色寄りに沈める（ガイドのハイライト用）。 */
  focus?: ReadonlySet<number> | null;
}

/**
 * フォーカス外のブロックに掛ける明るさ（sRGB空間、見た目の明るさ）。
 * リニア変換前に掛けることで、画面上でもこの比率まで暗く見える。
 */
const FOCUS_DIM = 0.35;

/**
 * 構造データから、見える面だけを集めたジオメトリを組み立てる。
 * 隣接する不透明ブロックに隠れる面は捨て、頂点カラーへ面の陰影とAOを焼き込む。
 */
export function buildStructureGeometry(
  model: StructureModel,
  appearances: BlockAppearance[],
  atlas: TextureAtlas,
  options: BuildOptions = {},
): BuiltGeometry {
  const mode = options.mode ?? 'texture';
  const useAmbientOcclusion = options.ambientOcclusion ?? true;
  const focus = options.focus && options.focus.size > 0 ? options.focus : null;
  const [sizeX, sizeY, sizeZ] = model.size;
  const grid = new Int32Array(sizeX * sizeY * sizeZ);
  const index = (x: number, y: number, z: number) => (y * sizeZ + z) * sizeX + x;

  for (const block of model.blocks) {
    grid[index(block.position[0], block.position[1], block.position[2])] = block.state + 1;
  }

  const stateAt = (x: number, y: number, z: number): number => {
    if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) return -1;
    return grid[index(x, y, z)] - 1;
  };
  const occludesAt = (x: number, y: number, z: number): boolean => {
    const state = stateAt(x, y, z);
    return state >= 0 && appearances[state].occludes;
  };

  const solid = new MeshBuffers();
  const translucent = new MeshBuffers();
  const centerX = sizeX / 2;
  const centerY = sizeY / 2;
  const centerZ = sizeZ / 2;
  const fallbackSlot = atlas.slots.values().next().value as AtlasSlot;

  for (let blockIndex = 0; blockIndex < model.blocks.length; blockIndex += 1) {
    const block = model.blocks[blockIndex];
    const [x, y, z] = block.position;
    const appearance = appearances[block.state];
    const buffers = appearance.layer === 'translucent' ? translucent : solid;
    const paletteEntry = model.palette[block.state];
    const tint = mode === 'color' ? paletteEntry.color : 0xffffff;
    const dim = focus && !focus.has(block.state) ? FOCUS_DIM : 1;
    const tintRed = srgbToLinear((((tint >> 16) & 255) / 255) * dim);
    const tintGreen = srgbToLinear((((tint >> 8) & 255) / 255) * dim);
    const tintBlue = srgbToLinear(((tint & 255) / 255) * dim);

    if (appearance.quads) {
      for (const quad of appearance.quads) {
        if (quad.cullface !== undefined) {
          const [dx, dy, dz] = NEIGHBOR[quad.cullface];
          const neighborState = stateAt(x + dx, y + dy, z + dz);
          if (neighborState >= 0 && (appearances[neighborState].occludes
            || (appearance.mergesWithSame && model.palette[neighborState].name === paletteEntry.name))) continue;
        }
        const slot = atlas.slots.get(quad.texture) ?? fallbackSlot;
        const uv = quad.uv.map((value, i) => i % 2 === 0
          ? slot.u0 + (slot.u1 - slot.u0) * value
          : slot.v1 - (slot.v1 - slot.v0) * value);
        const corners = quad.corners.map((value, i) => value + [x-centerX, y-centerY, z-centerZ][i%3]);
        const shade = linearShade(appearance.emissive ? 0.8 + 0.2 * quad.shade : quad.shade);
        const color = mode === 'texture' ? quad.tint ?? 0xffffff : 0xffffff;
        buffers.addQuad(corners, uv, [shade,shade,shade,shade],
          tintRed * srgbToLinear(((color>>16)&255)/255),
          tintGreen * srgbToLinear(((color>>8)&255)/255),
          tintBlue * srgbToLinear((color&255)/255), blockIndex);
      }
      continue;
    }

    if (appearance.cross) {
      const slot = atlas.slots.get(appearance.faces[0]) ?? fallbackSlot;
      const uv = faceUv(4, { from: [0, 0, 0], to: [1, 1, 1] }, slot);
      const shade = linearShade(1);
      const shades = [shade, shade, shade, shade];
      for (const quad of crossQuads(x - centerX, y - centerY, z - centerZ)) {
        buffers.addQuad(quad, uv, shades, tintRed, tintGreen, tintBlue, blockIndex);
      }
      continue;
    }

    for (const box of appearance.boxes) {
      for (let face = 0; face < 6; face += 1) {
        const axis = face >> 1;
        const positive = face % 2 === 0;
        const boundary = positive
          ? box.to[axis] >= 1
          : box.from[axis] <= 0;

        if (boundary) {
          const [dx, dy, dz] = NEIGHBOR[face];
          const neighborState = stateAt(x + dx, y + dy, z + dz);
          if (neighborState >= 0) {
            const neighbor = appearances[neighborState];
            if (neighbor.occludes) continue;
            if (appearance.mergesWithSame && model.palette[neighborState].name === paletteEntry.name) continue;
          }
        }

        const slot = atlas.slots.get(appearance.faces[face]) ?? fallbackSlot;
        const corners = faceCorners(face, box, x - centerX, y - centerY, z - centerZ);
        const uv = faceUv(face, box, slot);
        const baseShade = appearance.emissive ? Math.min(1, FACE_SHADE[face] * 0.35 + 0.65) : FACE_SHADE[face];

        const shades = [0, 0, 0, 0];
        const applyAmbientOcclusion = useAmbientOcclusion && boundary && appearance.occludes && !appearance.emissive;
        if (applyAmbientOcclusion) {
          const [dx, dy, dz] = NEIGHBOR[face];
          const [uAxis, vAxis] = TANGENTS[face];
          const signs = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
          for (let corner = 0; corner < 4; corner += 1) {
            const [uSign, vSign] = signs[corner];
            const ux = x + dx + uAxis[0] * uSign;
            const uy = y + dy + uAxis[1] * uSign;
            const uz = z + dz + uAxis[2] * uSign;
            const vx = x + dx + vAxis[0] * vSign;
            const vy = y + dy + vAxis[1] * vSign;
            const vz = z + dz + vAxis[2] * vSign;
            const side1 = occludesAt(ux, uy, uz);
            const side2 = occludesAt(vx, vy, vz);
            const cornerBlocked = occludesAt(
              x + dx + uAxis[0] * uSign + vAxis[0] * vSign,
              y + dy + uAxis[1] * uSign + vAxis[1] * vSign,
              z + dz + uAxis[2] * uSign + vAxis[2] * vSign,
            );
            const level = side1 && side2 ? 0 : 3 - (Number(side1) + Number(side2) + Number(cornerBlocked));
            shades[corner] = linearShade(baseShade * AO_SHADE[level]);
          }
        } else {
          const shade = linearShade(baseShade);
          shades[0] = shade;
          shades[1] = shade;
          shades[2] = shade;
          shades[3] = shade;
        }

        buffers.addQuad(corners, uv, shades, tintRed, tintGreen, tintBlue, blockIndex);
      }
    }
  }

  const solidGeometry = solid.toGeometry();
  const translucentGeometry = translucent.toGeometry();
  return {
    solid: solidGeometry,
    translucent: translucentGeometry,
    faceCount: (solidGeometry?.faceCount ?? 0) + (translucentGeometry?.faceCount ?? 0),
  };
}
